/**
 * One lobby table: seats, the waiting line, Ready, rematch, the turn clock and the Oracle.
 *
 * Ported from the original server's GameRoom (src/room.js) as pure functions over a plain record, so
 * the record can live in Postgres and any replica can advance it. Nothing here does I/O or reads the
 * clock: the host passes a `ctx` with
 *   now        epoch ms
 *   rand()     uniform [0, 1), from a CSPRNG
 *   present    Set of tokens with a live stream to this table
 *   lastSeen   Map token -> epoch ms of the last stream heartbeat
 *   identity   (token) => {name, pfp} the server verified for that token, or null
 *   oracle     (maskedView, seed, level) => move
 *
 * Tokens are the server's hashes of an httpOnly cookie; a client never names itself. Names and
 * pictures come from `ctx.identity`, never from a message.
 */
import { BY_ID, CARDS } from "./cards.js";
import {
  newState,
  legalMoves,
  applyMove,
  isOver,
  findLegal,
  serialise,
  deserialise,
  RARITIES,
  DEFAULT_CURVE,
} from "./engine.js";
import { maskedViewForAI } from "./mask.js";

/** A message the rules refuse. Its text is shown to the player who sent it; nothing else is. */
export class Refusal extends Error {
  /** `code` names a refusal the page acts on (e.g. "verify": show the Turnstile check). */
  constructor(message, fatal = false, code = null) {
    super(message);
    this.fatal = fatal;
    this.code = code;
  }
}

export const TIMING = Object.freeze({
  turnMs: 60_000, // a human's turn; then a random legal move is played for them
  holdMs: 120_000, // a dropped player's seat is held this long, then they forfeit
  breakMs: 10_000, // after a game, how long the result shows before a waiting player takes a seat
  readyMs: 60_000, // two humans in the lobby: whoever has not pressed Ready by then goes back to watching
  // (and after a game: whoever has not answered Play again by then gives up the seat)
  oracleMs: 250, // the Oracle's pause before it moves
});
export const MAX_WAITING = 60;
/**
 * Fixed tables: the numbered tables Otherside's world opens at /instant/<n>
 * (one per in-world table), stored as FIXED_TABLE_BASE + n so the lobby never
 * hands them out. As at Otherside's blackjack tables, an arrival sits if a seat
 * is free and otherwise watches: there is no line, and a third player who wants
 * to play walks to another table. A lone player waits for a challenger or plays
 * the Oracle; there is no Start new game.
 */
export const FIXED_TABLE_BASE = 10_000; // above the lobby's 1..LOBBY_MAX_TABLES
export const MAX_FIXED_TABLES = 99;
export const isFixedTable = (id) =>
  id > FIXED_TABLE_BASE && id <= FIXED_TABLE_BASE + MAX_FIXED_TABLES;
export const RULES = Object.freeze({ ante: false, advanced: false });
const ORACLE = "oracle";

/**
 * @typedef {{ token: string, name: string | null, pfp: string | null, ai: string | null,
 *   deck: string[] | null, connected: boolean, goneAt?: number | null,
 *   account?: string | null, wallet?: string | null, source?: string | null,
 *   privyUserId?: string | null, hardWins?: number, holds?: boolean, level?: string,
 *   ranked?: boolean }} Seat
 * @typedef {{ gameNo: number, rated: boolean,
 *   ratings: ({ rating: number, delta: number } | null)[], resultId?: string,
 *   unlocked?: { seat: number, level: number, wins: number, held: boolean },
 *   capped?: number }} Outcome
 * @typedef {{ toMove: number, log: { p: number, kind: string, auto?: boolean, at?: number }[] } & Record<string, unknown>} Game
 * @typedef {{ table: number, phase: "lobby" | "play" | "over", seats: (Seat | null)[],
 *   waiting: { token: string }[], ready: boolean[], rematch: boolean[], game: Game | null,
 *   gameNo: number, firstSeat: number, turnDeadline: number | null, aiAt: number | null,
 *   overAt: number | null, pairedAt: number | null, forfeit: { seat: number, name: string } | null,
 *   unseated: string[], reservation: { token: string, until: number } | null,
 *   startedAt?: number | null, outcome?: Outcome | null }} Table
 */

/** @param {number} table @returns {Table} */
export function emptyTable(table) {
  return {
    table,
    phase: "lobby",
    seats: [null, null],
    waiting: [],
    ready: [false, false],
    rematch: [false, false],
    game: null,
    gameNo: 0,
    firstSeat: 0,
    turnDeadline: null,
    aiAt: null,
    overAt: null,
    pairedAt: null,
    rematchAt: null,
    forfeit: null,
    unseated: [],
    reservation: null,
    code: null, // a private match's join code (lobby.ts); null for an open table
    audience: 0, // people watching the table, refreshed from presence on every tick
  };
}

/** A hand on the shared rarity curve, shuffled with the injected CSPRNG. */
export function randomDeck(rand) {
  const out = [];
  RARITIES.forEach((r, i) => {
    const pool = CARDS.filter((c) => c.r === r);
    for (let k = pool.length - 1; k > 0; k--) {
      const j = Math.floor(rand() * (k + 1));
      [pool[k], pool[j]] = [pool[j], pool[k]];
    }
    out.push(...pool.slice(0, DEFAULT_CURVE[i]).map((c) => c.id));
  });
  return out;
}

export function randomLegalMove(g, seat, rand) {
  const all = legalMoves(g, seat);
  const plays = all.filter((m) => m.kind !== "pass");
  // a face-up play (place or upgrade) before a rock: a timed-out player's
  // Level IV should not be spent face down
  const faceUp = plays.filter((m) => m.kind !== "rock");
  const pool = faceUp.length ? faceUp : plays.length ? plays : all;
  return pool[Math.floor(rand() * pool.length)];
}

const seatOf = (r, token) =>
  r.seats.findIndex((s) => s && !s.ai && s.token === token);
// the Oracle's difficulty, picked by the player (game/oracle.js ORACLE_SLIP); Hard by default
const ORACLE_LEVELS = new Set(["easy", "normal", "hard"]);
const oracleSeat = (i, level) => ({
  token: "ai:" + i,
  name: "Oracle",
  pfp: null,
  ai: ORACLE,
  level: ORACLE_LEVELS.has(level) ? level : "hard",
  connected: true,
  deck: null,
});
/** Hard Oracle wins that unlock foil art, one level at a time: 15 wins foil Level I cards, 25 adds
 *  Level II, and so on up to a full foil hand at 100. Foils are art only; every number stays the same. */
export const FOIL_WINS = [15, 25, 50, 75, 100];
/** How many levels a player's foils cover (0–5), from their Hard Oracle wins. */
export const foilLevels = (wins) =>
  FOIL_WINS.filter((w) => (wins || 0) >= w).length;

const humanSeat = (token, ctx) => {
  const id = ctx.identity(token);
  return {
    token,
    name: id?.name ?? null,
    pfp: id?.pfp ?? null,
    hardWins: id?.hardWins ?? 0,
    holds: !!id?.holds,
    deck: null,
    ai: null,
    connected: true,
    goneAt: null,
  };
};
/** A lobby table can seat a newcomer now: no game running, no Oracle, and an empty seat. */
export const seatFree = (r) =>
  r.phase !== "play" &&
  r.seats.every((s) => !s || !s.ai) &&
  r.seats.some((s) => !s);

/** Bring seats and the line in line with who has a live stream. */
function syncPresence(r, ctx) {
  r.seats.forEach((s, i) => {
    if (!s || s.ai) return;
    const live = ctx.present.has(s.token);
    if (s.connected && !live) {
      s.connected = false;
      s.goneAt = ctx.lastSeen.get(s.token) ?? ctx.now;
      r.ready[i] = false; // walking back in means pressing Ready again
    } else if (!s.connected && live) {
      s.connected = true;
      s.goneAt = null;
    }
  });
  r.waiting = r.waiting.filter((w) => ctx.present.has(w.token));
  if (ctx.here)
    r.audience = [...ctx.here].filter((t) => seatOf(r, t) === -1).length;
  r.unseated = r.unseated.filter((t) => ctx.present.has(t));
}

/** Apply one client message from `token`. Throws an Error whose message is shown to that client. */
export function handle(r, token, m, ctx) {
  const playing = r.phase === "play";
  syncPresence(r, ctx);
  let reply = null;
  switch (m && m.t) {
    case "hello":
      reply = hello(r, token, m, ctx);
      break;
    case "name":
      refreshIdentity(r, token, ctx);
      break;
    case "move":
      move(r, token, m, ctx);
      break;
    case "ready": {
      const seat = requireSeat(r, token);
      if (r.phase !== "lobby")
        throw new Refusal("the game has already started");
      const other = r.seats[1 - seat];
      if (!other || other.ai) throw new Refusal("waiting for a challenger");
      r.ready[seat] = m.on !== false;
      break;
    }
    case "rematch": {
      const seat = requireSeat(r, token);
      if (r.phase !== "over") throw new Refusal("the game is not finished");
      if (r.seats[1 - seat]?.ai)
        needsVerify(r.seats[1 - seat].level, token, ctx);
      r.rematch[seat] = true;
      if (r.seats[1 - seat]?.ai) r.rematch[1 - seat] = true;
      r.rematchAt ??= ctx.now;
      break;
    }
    case "playOracle": {
      const seat = requireSeat(r, token);
      if (r.phase === "play")
        throw new Refusal("a game is already in progress");
      // a private match is for the friend with its code; the Oracle never takes their seat
      if (r.code) throw new Refusal("this table is for your friend");
      needsVerify(m.level, token, ctx);
      const other = r.seats[1 - seat];
      // an opponent who will not play (no Ready, no Play again) goes back to watching
      const willing =
        r.phase === "lobby" ? r.ready[1 - seat] : r.rematch[1 - seat];
      if (other && !other.ai && willing)
        throw new Refusal("the other seat is taken");
      if (other && !other.ai) r.unseated.push(other.token);
      r.ready = [false, false];
      r.rematch = [false, false];
      r.seats[1 - seat] = oracleSeat(1 - seat, m.level);
      start(r, ctx);
      break;
    }
    case "leave":
      leave(r, token);
      break;
    case "forfeit": {
      // give up the game being played: the other seat wins, as when a dropped seat runs out
      const seat = requireSeat(r, token);
      if (r.phase !== "play") throw new Refusal("no game is being played");
      const s = r.seats[seat];
      end(r, ctx, {
        seat,
        name: s.name || `Player ${seat + 1}`,
        conceded: true,
      });
      break;
    }
    case "join": {
      if (seatOf(r, token) !== -1 || r.waiting.some((w) => w.token === token))
        break;
      if (!seatFree(r))
        throw new Refusal(
          isFixedTable(r.table)
            ? "Both seats are taken. Try another table."
            : "This table is full. Start a new game instead."
        );
      r.waiting.push({ token });
      r.unseated = r.unseated.filter((t) => t !== token);
      break;
    }
    default:
      return { reply: null };
  }
  sync(r, ctx, playing);
  return { reply };
}

/** A queue match (game/queue.js): both players accepted, so they sit and the game deals at once,
 *  with no Ready check. Whoever has not opened the table yet is held like any dropped seat. */
export function seatPair(r, tokens, ctx) {
  r.seats = tokens.map((t) => humanSeat(t, ctx));
  start(r, ctx);
}

function hello(r, token, m, ctx) {
  if (r.reservation?.token === token) r.reservation = null;
  const seat = seatOf(r, token);
  if (seat !== -1) {
    Object.assign(r.seats[seat], { connected: true, goneAt: null });
    refreshIdentity(r, token, ctx);
    return null;
  }
  if (r.waiting.some((w) => w.token === token)) return null;
  // At a fixed table an arrival sits if a seat is free and otherwise watches.
  // In the lobby a watcher stays a watcher, and somebody who came to play but
  // found the table taken is told so, to be sent to another table.
  if (isFixedTable(r.table)) {
    if (!seatFree(r)) return null;
  } else if (m.intent === "watch" || !seatFree(r))
    return m.intent === "watch" ? null : { t: "lobbyFull" };
  if (r.waiting.length >= MAX_WAITING)
    throw new Refusal("This table is full.", true);
  r.waiting.push({ token });
  r.unseated = r.unseated.filter((t) => t !== token);
  return null;
}

/** The bot check flagged this account (src/lib/game/bot-check.ts): a Hard game waits for a Turnstile
 *  pass. Everyone else, and every other level, plays on. */
function needsVerify(level, token, ctx) {
  if ((level ?? "hard") === "hard" && ctx.identity(token)?.mustVerify)
    throw new Refusal(
      "Confirm you're a person to play the Hard Oracle.",
      false,
      "verify"
    );
}

function refreshIdentity(r, token, ctx) {
  const s = r.seats[seatOf(r, token)];
  if (!s) return;
  const id = ctx.identity(token);
  s.name = id?.name ?? null;
  s.pfp = id?.pfp ?? null;
  s.hardWins = id?.hardWins ?? 0;
  s.holds = !!id?.holds;
}

function requireSeat(r, token) {
  const seat = seatOf(r, token);
  if (seat === -1) throw new Refusal("you are not seated");
  return seat;
}

function move(r, token, m, ctx) {
  const seat = requireSeat(r, token);
  if (r.phase !== "play") throw new Refusal("no game in progress");
  const g = deserialise(r.game);
  if (g.toMove !== seat) throw new Refusal("not your turn");
  // a stale click: the client echoes the move number and the card it believes it is playing
  if (typeof m.seq === "number" && m.seq !== g.log.length)
    throw new Refusal("that move was out of date — try again");
  const mv = findLegal(g, { kind: m.kind, hi: m.hi, slot: m.slot });
  if (!mv) throw new Refusal("illegal move");
  if (m.cardId && mv.kind !== "pass" && g.hands[seat][mv.hi].id !== m.cardId)
    throw new Refusal("that move was out of date — try again");
  applyMove(g, mv);
  r.game = serialise(g);
  afterMove(r, g, ctx);
}

function leave(r, token) {
  const seat = seatOf(r, token);
  if (seat !== -1) {
    if (r.phase === "play")
      throw new Refusal("finish the game before leaving the table");
    r.seats[seat] = null;
    if (r.seats[1 - seat]?.ai) r.seats[1 - seat] = null;
    r.rematch = [false, false];
    r.ready = [false, false];
    if (!r.seats[0] && !r.seats[1]) reset(r);
  } else r.waiting = r.waiting.filter((w) => w.token !== token);
}

function start(r, ctx) {
  r.rematchAt = null;
  for (const s of r.seats) {
    s.deck = randomDeck(ctx.rand);
    // who the game counts for is fixed at the deal: a later login cannot claim it
    const id = s.ai ? null : ctx.identity(s.token);
    s.account = id?.account ?? null;
    s.wallet = id?.wallet ?? null;
    s.source = id?.source ?? null;
    s.privyUserId = id?.privyUserId ?? null;
    s.ranked = !!id?.ranked;
    // the foil tier is dealt with the hand: a rematch after a 15th Hard win deals the first foils
    if (!s.ai) {
      s.hardWins = id?.hardWins ?? 0;
      s.holds = !!id?.holds;
    }
  }
  const hands = r.seats.map((s) => s.deck.map((id) => BY_ID[id]));
  r.game = serialise(
    newState(hands[0], hands[1], r.firstSeat, [1, 1], {
      rules: { ...RULES },
      rand: ctx.rand,
    })
  );
  Object.assign(r, {
    gameNo: r.gameNo + 1,
    phase: "play",
    rematch: [false, false],
    ready: [false, false],
    pairedAt: null,
    forfeit: null,
    overAt: null,
    firstSeat: 1 - r.firstSeat,
    startedAt: ctx.now,
    outcome: null,
  });
  armTurn(r, ctx);
}

function armTurn(r, ctx) {
  r.turnDeadline = null;
  r.aiAt = null;
  if (r.phase !== "play" || !r.game) return;
  if (r.seats[r.game.toMove]?.ai) r.aiAt = ctx.now + TIMING.oracleMs;
  else r.turnDeadline = ctx.now + TIMING.turnMs;
}

function afterMove(r, g, ctx) {
  // when each move was made (ms): the bot check (src/lib/game/bot-check.ts) reads how long a player
  // thinks; r.game.log is this same array, so the stamp is stored with it
  const last = g.log[g.log.length - 1];
  if (last && last.at == null) last.at = ctx.now;
  if (isOver(g)) end(r, ctx);
  else armTurn(r, ctx);
}

function end(r, ctx, forfeit = null) {
  Object.assign(r, {
    phase: "over",
    overAt: ctx.now,
    turnDeadline: null,
    aiAt: null,
    rematch: [false, false],
    forfeit,
  });
}

function reset(r) {
  Object.assign(r, {
    code: null, // an emptied private table opens to everyone again
    phase: "lobby",
    game: null,
    forfeit: null,
    overAt: null,
    turnDeadline: null,
    aiAt: null,
    ready: [false, false],
  });
}

/** How long a dropped player's seat is held. At an in-world table, walking away is leaving: with
 *  no game being played the seat opens at once, so the next player is not left watching a
 *  finished game for the whole hold. */
const holdFor = (r) =>
  isFixedTable(r.table) && r.phase !== "play" ? 0 : TIMING.holdMs;
export const holdUntil = (r, s) => s.goneAt + holdFor(r);

const yieldable = (r) => r.seats.some((s) => !s || s.ai);
const breakDone = (r, now) =>
  r.phase !== "over" ||
  now >= (r.overAt || 0) + TIMING.breakMs ||
  r.seats.some((s, i) => s && !s.ai && r.rematch[i]);

/** Advance the table to `ctx.now`: forfeits, the turn clock, the Oracle, seating and starting.
 *  A game that ends stops the pass, with its players still seated: the host records the result
 *  from this record, and only a later pass may open the seats or reset the table. */
export function sync(r, ctx, playing = r.phase === "play") {
  syncPresence(r, ctx);
  for (let guard = 0; guard < 40; guard++)
    if ((playing && r.phase !== "play") || !step(r, ctx)) break;
}

function step(r, ctx) {
  const now = ctx.now;
  // 1. a dropped player's held seat runs out: they forfeit (still seated, so the result counts
  //    for them) and on a later pass the seat opens
  for (const i of [0, 1]) {
    const s = r.seats[i];
    if (!s || s.ai || s.connected || s.goneAt == null || now < holdUntil(r, s))
      continue;
    if (r.phase === "play") {
      end(r, ctx, { seat: i, name: s.name || `Player ${i + 1}` });
      return true;
    }
    r.seats[i] = null;
    if (r.seats[1 - i]?.ai) r.seats[1 - i] = null; // the Oracle only ever sits opposite a human
    r.rematch = [false, false];
    r.ready = [false, false];
    if (!r.seats[0] && !r.seats[1]) reset(r);
    return true;
  }
  if (r.phase === "play") {
    const g = deserialise(r.game);
    // 2. the turn clock ran out: a random legal move is played for them
    if (r.turnDeadline != null && now >= r.turnDeadline) {
      applyMove(g, randomLegalMove(g, g.toMove, ctx.rand));
      g.log[g.log.length - 1].auto = true;
      r.game = serialise(g);
      afterMove(r, g, ctx);
      return true;
    }
    // 3. the Oracle's move, from a view with the opponent's information removed
    if (r.aiAt != null && now >= r.aiAt) {
      const seat = g.toMove;
      const mv = ctx.oracle(
        maskedViewForAI(g, seat),
        Math.floor(ctx.rand() * 2 ** 32),
        r.seats[seat]?.level
      );
      applyMove(g, findLegal(g, mv || {}) || legalMoves(g, seat)[0]); // the engine stays the authority
      r.game = serialise(g);
      afterMove(r, g, ctx);
      return true;
    }
    return false;
  }
  // 4b. one player asked for Play again and the other never answered: the other seat opens, so
  //     the one who asked can play again (the Oracle, or the next challenger)
  if (
    r.phase === "over" &&
    r.rematchAt != null &&
    now >= r.rematchAt + TIMING.readyMs
  ) {
    const i = r.seats.findIndex((s, j) => s && !s.ai && !r.rematch[j]);
    r.rematchAt = null;
    if (i !== -1 && r.rematch[1 - i]) {
      r.unseated.push(r.seats[i].token);
      r.seats[i] = null;
      r.rematch = [false, false];
      return true;
    }
  }
  // 4a. two connected humans each have readyMs to press Ready; whoever has not goes back to watching
  const [p0, p1] = r.seats;
  if (
    r.phase === "lobby" &&
    p0 &&
    p1 &&
    !p0.ai &&
    !p1.ai &&
    p0.connected &&
    p1.connected
  ) {
    if (r.pairedAt == null) r.pairedAt = now;
    if (now >= r.pairedAt + TIMING.readyMs && !(r.ready[0] && r.ready[1])) {
      for (const i of [0, 1])
        if (!r.ready[i]) {
          r.unseated.push(r.seats[i].token);
          r.seats[i] = null;
        }
      r.ready = [false, false];
      r.pairedAt = null;
      if (!r.seats[0] && !r.seats[1]) reset(r);
      return true;
    }
  } else r.pairedAt = null;
  // 4. seat the next waiting player: into an empty seat, or in place of the Oracle
  if (r.waiting.length && yieldable(r) && breakDone(r, now)) {
    const w = r.waiting.shift();
    const empty = r.seats.findIndex((s) => !s);
    const i = empty !== -1 ? empty : r.seats.findIndex((s) => s.ai);
    r.seats[i] = humanSeat(w.token, ctx);
    if (r.seats[1 - i]?.ai) r.seats[1 - i] = null; // a new human pair, never human + stale Oracle
    Object.assign(r, {
      phase: "lobby",
      game: null,
      forfeit: null,
      overAt: null,
      rematch: [false, false],
      ready: [false, false],
    });
    return true;
  }
  // 5. start: a fresh pair once both are Ready, the same pair when both asked for a rematch
  const [a, b] = r.seats;
  if (a && b && (a.ai || a.connected) && (b.ai || b.connected)) {
    const ready = (a.ai || r.ready[0]) && (b.ai || r.ready[1]);
    if (
      (r.phase === "lobby" && ready) ||
      (r.phase === "over" && r.rematch[0] && r.rematch[1])
    ) {
      start(r, ctx);
      return true;
    }
  }
  return false;
}

/** The earliest moment `sync` has something to do, or null. The stream poller ticks the table then. */
export function nextDue(r) {
  const due = [];
  if (r.phase === "play") due.push(r.aiAt, r.turnDeadline);
  for (const s of r.seats)
    if (s && !s.ai && !s.connected && s.goneAt != null)
      due.push(holdUntil(r, s));
  if (r.phase === "over" && r.waiting.length && yieldable(r))
    due.push((r.overAt || 0) + TIMING.breakMs);
  if (r.phase === "lobby" && r.pairedAt != null)
    due.push(r.pairedAt + TIMING.readyMs);
  if (r.phase === "over" && r.rematchAt != null)
    due.push(r.rematchAt + TIMING.readyMs);
  const live = due.filter((x) => x != null);
  return live.length ? Math.min(...live) : null;
}

/** What the lobby router needs. Tokens stay server-side. */
export function summary(r, watchers) {
  return {
    table: r.table,
    phase: r.phase,
    code: r.code ?? null,
    watchers,
    moves: r.moves ?? r.game?.log?.length ?? 0, // the lobby reads a stored count
    waiting: r.waiting.map((w) => w.token),
    seats: r.seats.map(
      (s, i) =>
        s && {
          token: s.ai ? null : s.token,
          ai: s.ai,
          connected: !!s.connected,
          name: s.name || (s.ai ? "Oracle" : `Player ${i + 1}`),
        }
    ),
  };
}
